import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Sync as SyncIcon,
  InsertDriveFile as FileIcon,
  CheckCircle as ReadyIcon,
  Error as ErrorIcon,
  HourglassEmpty as PendingIcon,
} from '@mui/icons-material';
import { agentService } from '../../services/agentService';
import { useNotificationStore } from '../../store/notificationStore';
import type { AgentKnowledgeFileDto } from '../../types/knowledgeFile';
import { KnowledgeFileStatus, KnowledgeFileStatusLabel } from '../../types/knowledgeFile';

interface AgentKnowledgeFileListProps {
  agentId: string;
  readOnly?: boolean;
}

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

const StatusChip = ({ status, syncError }: { status: KnowledgeFileStatus; syncError?: string | null }) => {
  const label = KnowledgeFileStatusLabel[status] ?? 'Unknown';
  const color =
    status === KnowledgeFileStatus.Ready
      ? 'success'
      : status === KnowledgeFileStatus.Failed
        ? 'error'
        : status === KnowledgeFileStatus.Uploading || status === KnowledgeFileStatus.Processing
          ? 'info'
          : 'default';
  const icon =
    status === KnowledgeFileStatus.Ready ? (
      <ReadyIcon />
    ) : status === KnowledgeFileStatus.Failed ? (
      <ErrorIcon />
    ) : (
      <PendingIcon />
    );

  const chip = <Chip label={label} color={color as any} size="small" icon={icon} variant="outlined" />;

  if (syncError) {
    return <Tooltip title={syncError}>{chip}</Tooltip>;
  }
  return chip;
};

export const AgentKnowledgeFileList = ({ agentId, readOnly = false }: AgentKnowledgeFileListProps) => {
  const [files, setFiles] = useState<AgentKnowledgeFileDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { addNotification } = useNotificationStore();

  const loadFiles = useCallback(async () => {
    if (!agentId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await agentService.getKnowledgeFiles(agentId);
      setFiles(data);
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to load knowledge files';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [agentId]);

  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  const handleUpload = async (fileList: FileList | File[]) => {
    const filesToUpload = Array.from(fileList);
    if (filesToUpload.length === 0) return;

    setUploading(true);
    let successCount = 0;
    for (const file of filesToUpload) {
      try {
        await agentService.uploadKnowledgeFile(agentId, file);
        successCount++;
      } catch (err: any) {
        addNotification(
          err.response?.data?.message || `Failed to upload "${file.name}"`,
          'error'
        );
      }
    }
    if (successCount > 0) {
      addNotification(`${successCount} file(s) uploaded`, 'success');
    }
    setUploading(false);
    loadFiles();
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      handleUpload(e.target.files);
      e.target.value = '';
    }
  };

  const handleDelete = async (file: AgentKnowledgeFileDto) => {
    if (!window.confirm(`Delete "${file.originalFileName}"? This will remove it from the provider and storage.`)) return;
    try {
      await agentService.deleteKnowledgeFile(agentId, file.id);
      addNotification('File deleted', 'success');
      loadFiles();
    } catch (err: any) {
      addNotification(err.response?.data?.message || 'Failed to delete file', 'error');
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      await agentService.syncKnowledgeFiles(agentId);
      addNotification('Knowledge files synced to provider', 'success');
      loadFiles();
    } catch (err: any) {
      addNotification(err.response?.data?.message || 'Failed to sync files', 'error');
    } finally {
      setSyncing(false);
    }
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);
      if (readOnly || uploading) return;
      if (e.dataTransfer.files) {
        handleUpload(e.dataTransfer.files);
      }
    },
    [readOnly, uploading, agentId]
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Knowledge Files (RAG)</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {!readOnly && (
            <>
              <Button
                variant="outlined"
                size="small"
                startIcon={syncing ? <CircularProgress size={16} /> : <SyncIcon />}
                onClick={handleSync}
                disabled={syncing || files.length === 0}
              >
                Sync
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={uploading ? <CircularProgress size={16} /> : <UploadIcon />}
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                Upload
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                hidden
                onChange={handleFileInput}
                accept=".pdf,.txt,.md,.docx,.doc,.csv,.json,.html,.xml,.pptx,.xlsx"
              />
            </>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {uploading && <LinearProgress sx={{ mb: 2 }} />}

      <Box
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        sx={{
          border: isDragOver ? '2px dashed' : '2px dashed transparent',
          borderColor: isDragOver ? 'primary.main' : 'transparent',
          borderRadius: 1,
          bgcolor: isDragOver ? 'action.hover' : 'transparent',
          transition: 'all 0.2s ease',
          minHeight: files.length === 0 ? 160 : 'auto',
          display: 'flex',
          flexDirection: 'column',
          alignItems: files.length === 0 ? 'center' : 'stretch',
          justifyContent: files.length === 0 ? 'center' : 'flex-start',
        }}
      >
        {files.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <FileIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary" paragraph>
              No knowledge files yet. Upload files to enable RAG-powered search during agent conversations.
            </Typography>
            <Typography variant="caption" color="text.disabled">
              Drag and drop files here, or use the Upload button. Supported: PDF, TXT, MD, DOCX, CSV, JSON, HTML, PPTX, XLSX
            </Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>File Name</TableCell>
                  <TableCell>Size</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Provider</TableCell>
                  <TableCell>Uploaded</TableCell>
                  {!readOnly && <TableCell align="right" width={80}>Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {files.map((file) => (
                  <TableRow key={file.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FileIcon fontSize="small" color="action" />
                        <Tooltip title={file.originalFileName}>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                            {file.originalFileName}
                          </Typography>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatFileSize(file.fileSizeBytes)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <StatusChip status={file.status} syncError={file.syncError} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {file.providerName ?? '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {file.createdAt ? new Date(file.createdAt).toLocaleDateString() : '-'}
                      </Typography>
                    </TableCell>
                    {!readOnly && (
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => handleDelete(file)} color="error" aria-label="Delete">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Box>
  );
};
